import { useState } from 'react';
import { formatDurationCompact } from '../../../../config/global';
import { useUpdateMediaDuration } from '../../../../queries';
import { useUpdatePlaylistContentSettings } from '../../../../queries/playlistContent';
import { useQueryClient } from 'react-query';

/**
 * Builder-only tap-to-edit duration — .claude/pp-campaign-builder-prototype.html's
 * `.dur`/`.dur-in` (dashed-underline text → inline number input on click,
 * Enter/blur saves). Functionally equivalent to MediaDuration.jsx, restyled
 * for the compact `pp-cb-item` row and reusing its SAME hooks/endpoints —
 * MediaDuration.jsx itself is left untouched (still used by the non-builder
 * callers of CampaignCard.jsx/PlaylistCard.jsx).
 *
 * `source` picks which save path (F6):
 *  - "campaign": PUT /campaign/playlist-media/{id}/update {total_duration, layer}
 *    — MAIN rows (all types), via useUpdateMediaDuration (MediaDuration.jsx's
 *    own hook).
 *  - "playlist": POST /playlist/media/setting {playlist_media_id, duration}
 *    — vibes IMAGE rows inside the auto-playlist, via
 *    useUpdatePlaylistContentSettings (PlaylistCard.jsx's own hook).
 *
 * `guard` (optional): the playlist-accordion rows (PlaylistAccordionRow.jsx)
 * pass this to gate a SHARED playlist's first edit this page-session behind
 * a confirm dialog — called with a `proceed` callback instead of opening the
 * inline editor immediately; non-shared callers omit it and behave exactly
 * as before. `onSaved` (optional): fired after a successful commit, for a
 * caller that needs to invalidate something extra of its own (e.g. the
 * campaign's own publish-dirty banner) beyond this component's own
 * invalidation above.
 */
function BuilderDuration({ seconds, editable, source, id, layer, playlistId, playgroundLayer = 'PLAYGROUND', guard, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(seconds);
  const queryClient = useQueryClient();

  const updateCampaignDuration = useUpdateMediaDuration();
  const updatePlaylistItemSetting = useUpdatePlaylistContentSettings();
  const loading = updateCampaignDuration.isLoading || updatePlaylistItemSetting.isLoading;

  if (!editable) {
    return <span className="num">{formatDurationCompact(seconds)}</span>;
  }

  const commit = async () => {
    setEditing(false);
    const v = Number(value);
    if (!(v > 0) || v === seconds) return;

    if (source === 'campaign') {
      await updateCampaignDuration.mutateAsync({
        id,
        data: { total_duration: v, layer },
      });
    } else {
      await updatePlaylistItemSetting.mutateAsync(
        { playlist_media_id: id, duration: v },
        {
          onSuccess: () => {
            if (playlistId) {
              queryClient.invalidateQueries(['getPlaylistLayerContent', playlistId, playgroundLayer]);
            }
          },
        }
      );
    }
    onSaved?.();
  };

  if (editing) {
    return (
      <input
        type="number"
        min="1"
        autoFocus
        className="pp-cb-dur-in"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setValue(seconds); setEditing(false); }
        }}
        disabled={loading}
      />
    );
  }

  return (
    <button
      type="button"
      className="pp-cb-dur"
      onClick={(e) => {
        e.stopPropagation();
        const openEditor = () => { setValue(seconds); setEditing(true); };
        if (guard) guard(openEditor); else openEditor();
      }}
    >
      {formatDurationCompact(seconds)}
    </button>
  );
}

export default BuilderDuration;
